'use client'

/**
 * Break Reminder System
 * =====================
 * Pausenhinweis nach einem Arbeitsblock.
 * Token-basiert — Dark + Light aus Theme-Tokens, keine purple-/gray-Hardcodes.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { BackendAuth } from '@/lib/auth-backend'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/hooks/useTranslation'
import {
  Eye,
  Activity,
  Lightbulb,
  Heart,
  X,
  Clock,
  Smile,
  Zap,
  Droplets,
} from 'lucide-react'

const SUGGESTION_META = {
  eye_rest: { icon: Eye, titleKey: 'tickets.break.eye.title', title: '20-20-20 Regel' },
  stretch: { icon: Activity, titleKey: 'tickets.break.stretch.title', title: 'Bewegungspause' },
  hydration: { icon: Droplets, titleKey: 'tickets.break.water.title', title: 'Wasser trinken' },
  mental_break: { icon: Lightbulb, titleKey: 'tickets.break.mental.title', title: 'Mentale Erholung' },
  social: { icon: Smile, titleKey: 'tickets.break.social.title', title: 'Soziale Pause' },
}

export default function BreakReminderSystem({
  isVisible = false,
  onDismiss,
  onTakeBreak,
  workTime = 0,
  lastBreakTime = null,
}) {
  const { t } = useTranslation()
  const [currentSuggestion, setCurrentSuggestion] = useState(null)
  const [breakStats, setBreakStats] = useState({
    todayBreaks: 0,
    avgBreakDuration: 0,
    wellnessScore: 85,
  })
  const [showDetailed, setShowDetailed] = useState(false)

  const suggestions = {
    eye_rest: {
      id: 'eye_rest',
      ...SUGGESTION_META.eye_rest,
      description: t(
        'tickets.break.eye.desc',
        'Schaue 20 Sekunden lang auf etwas, das 6 Meter entfernt ist'
      ),
      duration: t('tickets.break.eye.dur', '30 Sekunden'),
      tips: [
        t('tickets.break.eye.tip1', 'Blinzele bewusst mehrmals'),
        t('tickets.break.eye.tip2', 'Schaue aus dem Fenster in die Ferne'),
        t('tickets.break.eye.tip3', 'Schließe die Augen für einen Moment'),
      ],
    },
    stretch: {
      id: 'stretch',
      ...SUGGESTION_META.stretch,
      description: t('tickets.break.stretch.desc', 'Stehe auf und mache leichte Dehnübungen'),
      duration: t('tickets.break.stretch.dur', '2-3 Minuten'),
      tips: [
        t('tickets.break.stretch.tip1', 'Schultern kreisen'),
        t('tickets.break.stretch.tip2', 'Nacken dehnen (links/rechts)'),
        t('tickets.break.stretch.tip3', 'Handgelenke lockern'),
        t('tickets.break.stretch.tip4', 'Kurz um den Stuhl gehen'),
      ],
    },
    hydration: {
      id: 'hydration',
      ...SUGGESTION_META.hydration,
      description: t(
        'tickets.break.water.desc',
        'Trinke ein Glas Wasser und prüfe deinen Flüssigkeitshaushalt'
      ),
      duration: t('tickets.break.water.dur', '1 Minute'),
      tips: [
        t('tickets.break.water.tip1', 'Mindestens 200ml Wasser trinken'),
        t('tickets.break.water.tip2', 'Bei Müdigkeit: zusätzliches Glas'),
        t('tickets.break.water.tip3', 'Koffein in Maßen'),
      ],
    },
    mental_break: {
      id: 'mental_break',
      ...SUGGESTION_META.mental_break,
      description: t('tickets.break.mental.desc', 'Kurze Meditation oder Atemübung'),
      duration: t('tickets.break.mental.dur', '2-5 Minuten'),
      tips: [
        t('tickets.break.mental.tip1', '4-7-8 Atemtechnik'),
        t('tickets.break.mental.tip2', 'Gedanken schweifen lassen'),
        t('tickets.break.mental.tip3', 'Kurze Dankbarkeitsübung'),
      ],
    },
    social: {
      id: 'social',
      ...SUGGESTION_META.social,
      description: t('tickets.break.social.desc', 'Kurzer Austausch mit Kollegen'),
      duration: t('tickets.break.social.dur', '3-5 Minuten'),
      tips: [
        t('tickets.break.social.tip1', 'Kleiner Plausch mit Kollegen'),
        t('tickets.break.social.tip2', 'Gemeinsam Kaffee holen'),
        t('tickets.break.social.tip3', 'Kurzes Teamupdate'),
      ],
    },
  }

  useEffect(() => {
    if (!isVisible) return

    const timeSinceLastBreak = lastBreakTime
      ? (Date.now() - new Date(lastBreakTime).getTime()) / (1000 * 60)
      : workTime

    let next = suggestions.eye_rest
    if (timeSinceLastBreak >= 20 && timeSinceLastBreak < 30) next = suggestions.eye_rest
    else if (timeSinceLastBreak >= 45 && timeSinceLastBreak < 60) next = suggestions.stretch
    else if (timeSinceLastBreak >= 60 && timeSinceLastBreak < 75) next = suggestions.hydration
    else if (timeSinceLastBreak >= 90) next = suggestions.mental_break
    else {
      const currentHour = new Date().getHours()
      if (currentHour >= 10 && currentHour <= 16 && timeSinceLastBreak >= 120) {
        next = suggestions.social
      }
    }

    setCurrentSuggestion(next)
  }, [isVisible, workTime, lastBreakTime])

  useEffect(() => {
    const loadBreakStats = async () => {
      try {
        const response = await BackendAuth.apiCallJson('/api/productivity/break-stats')
        setBreakStats(response)
      } catch (_err) {
        /* optional endpoint */
      }
    }

    if (isVisible) {
      loadBreakStats()
    }
  }, [isVisible])

  const extractDuration = (durationText) => {
    const match = String(durationText).match(/(\d+)/)
    return match ? parseInt(match[1], 10) : 5
  }

  const handleTakeBreak = useCallback(
    async (breakType) => {
      try {
        await BackendAuth.apiCallJson('/api/productivity/breaks', {
          method: 'POST',
          body: JSON.stringify({
            break_type: breakType.id,
            duration_planned: extractDuration(breakType.duration),
            started_at: new Date().toISOString(),
          }),
        })
        onTakeBreak?.(breakType)
      } catch (err) {
        console.error('Error logging break:', err)
        onTakeBreak?.(breakType)
      }
    },
    [onTakeBreak]
  )

  const handlePostpone = useCallback(async () => {
    try {
      await BackendAuth.apiCallJson('/api/productivity/breaks/postpone', {
        method: 'POST',
        body: JSON.stringify({
          postponed_at: new Date().toISOString(),
          reason: 'user_postponed',
        }),
      })
      onDismiss?.()
    } catch (err) {
      console.error('Error logging postponed break:', err)
      onDismiss?.()
    }
  }, [onDismiss])

  if (!isVisible || !currentSuggestion) return null

  const IconComponent = currentSuggestion.icon

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: 20 }}
        className="fixed top-4 right-4 z-[9999] max-w-md"
      >
        <Card className="border border-border bg-card shadow-xl">
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-primary/10 p-2">
                  <IconComponent className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    {t('tickets.break.title', 'Zeit für eine Pause')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('tickets.break.worked', {
                      defaultValue: '{n}min Arbeitszeit',
                      editable: false,
                    }).replace('{n}', String(Math.floor(workTime)))}
                  </p>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={onDismiss}
                className="min-h-11 min-w-11 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="mb-4">
              <h4 className="mb-2 font-medium text-primary">
                {t(currentSuggestion.titleKey, currentSuggestion.title)}
              </h4>
              <p className="mb-2 text-sm text-foreground">{currentSuggestion.description}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{currentSuggestion.duration}</span>
              </div>
            </div>

            <AnimatePresence>
              {showDetailed && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4"
                >
                  <h5 className="mb-2 text-sm font-medium text-foreground">
                    {t('tickets.break.tips', 'Tipps')}
                  </h5>
                  <ul className="space-y-1">
                    {currentSuggestion.tips.map((tip, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-xs text-muted-foreground"
                      >
                        <span className="mt-0.5 text-primary">•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mb-4 rounded-lg border border-border bg-black/[0.04] p-3 dark:bg-white/[0.06]">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-semibold text-foreground">
                    {breakStats.todayBreaks}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('tickets.break.today', 'Pausen heute')}
                  </p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-foreground">
                    {breakStats.avgBreakDuration}min
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('tickets.break.avg', 'Ø Dauer')}
                  </p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-foreground">
                    {breakStats.wellnessScore}%
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('tickets.break.wellness', 'Wellness')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => handleTakeBreak(currentSuggestion)}
                className="min-h-11 flex-1"
              >
                <Zap className="mr-2 h-4 w-4" />
                {t('tickets.break.start', 'Pause starten')}
              </Button>

              <Button variant="outline" onClick={handlePostpone} className="min-h-11">
                {t('tickets.break.later', 'Später')}
              </Button>
            </div>

            <button
              type="button"
              onClick={() => setShowDetailed(!showDetailed)}
              className="mt-3 w-full text-xs text-muted-foreground underline hover:text-foreground"
            >
              {showDetailed
                ? t('tickets.break.less', 'Weniger anzeigen')
                : t('tickets.break.more', 'Mehr Tipps anzeigen')}
            </button>
          </div>
        </Card>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="absolute -right-2 -bottom-2"
        >
          <div className="rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground shadow-lg">
            <Heart className="mr-1 inline h-3 w-3" />
            {t('tickets.break.healthy', 'Gesund arbeiten')}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
